import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
    Alert,
    Box,
    Chip,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import Logger from "../../../services/Logger";
import StatsWordWheelService from "../../../services/StatsWordWheelService";
import { CsvDownloadButton, csvNumber, downloadCsv } from "../../pzl_common/stats/adminCsvExport";
import { wordWheelPlayerPlaysPath } from "../play/AdminWordWheelPlayService";
import AdminWordWheelPackService from "../pack/AdminWordWheelPackService";
import { WORD_WHEEL_DEFAULT_SEASON, WORD_WHEEL_SEASON_NONE } from "../wordWheelLevelUtils";
import {
    WORD_WHEEL_PLAYER_SEARCH_DEBOUNCE_MS,
    WORD_WHEEL_PLAYER_SEARCH_PLACEHOLDER,
    toWordWheelPlayerSearchQuery,
    wordWheelGuestPlayerLabel,
} from "./wordWheelPlayerSearch";

const WORD_WHEEL_QUEST_APP_ID = "word_wheel_quest";
const WORD_WHEEL_QUEST_APP_NAME = "Word Wheel Quest";
const FALLBACK_PACKS = [
    { code: "season_2026", name: "Season 2026" },
    { code: "classic", name: "Classic" },
    { code: "hard_quest", name: "Hard Quest" },
    { code: "master", name: "Master" },
];

function packOptions(items) {
    const source = Array.isArray(items) && items.length > 0 ? items : FALLBACK_PACKS;
    return source
        .filter((pack) => pack?.code)
        .map((pack) => ({
            code: String(pack.code),
            name: pack.name || pack.code,
            locked: Boolean(pack.locked) || String(pack.code).startsWith("season_"),
        }))
        .sort((left, right) => {
            if (left.locked !== right.locked) {
                return left.locked ? -1 : 1;
            }
            return String(left.name).localeCompare(String(right.name));
        });
}

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatDateTime = (epochMillis) =>
    epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";

export default function WordWheelTopUsersPanel({ language }) {
    const [season, setSeason] = useState(WORD_WHEEL_DEFAULT_SEASON);
    const [packs, setPacks] = useState(FALLBACK_PACKS);
    const [excludeTest, setExcludeTest] = useState(true);
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [excludeNoEmail, setExcludeNoEmail] = useState(false);
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [language]);

    useEffect(() => {
        let cancelled = false;
        const loadPacks = async () => {
            try {
                const items = await AdminWordWheelPackService.list();
                if (!cancelled && Array.isArray(items) && items.length > 0) {
                    setPacks(packOptions(items));
                }
            } catch (err) {
                Logger.error("Failed to load word wheel packs for top users:", err);
            }
        };
        loadPacks();
        return () => {
            cancelled = true;
        };
    }, []);

    const resetPage = useCallback((update) => {
        update();
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, []);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            const next = toWordWheelPlayerSearchQuery(searchInput);
            setSearch((prev) => {
                if (prev === next) {
                    return prev;
                }
                setPageModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
                return next;
            });
        }, WORD_WHEEL_PLAYER_SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [searchInput]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await StatsWordWheelService.topUsers({
                    season,
                    language,
                    page: pageModel.page,
                    pageSize: pageModel.pageSize,
                    includeTest: !excludeTest,
                    excludeStaff,
                    excludeNoEmail,
                    q: search,
                });
                if (!cancelled) {
                    setResult(data ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load word wheel top users:", err);
                if (!cancelled) {
                    setError("Failed to load top users.");
                    setResult(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [season, language, excludeTest, excludeStaff, excludeNoEmail, pageModel.page, pageModel.pageSize, search]);

    const rows = useMemo(() => (Array.isArray(result?.users) ? result.users : []), [result]);
    const rowCount = Number(result?.total) || 0;
    const isNonSeason = season === WORD_WHEEL_SEASON_NONE;

    const handleDownloadCsv = useCallback(() => {
        const packLabel = isNonSeason
            ? "no-pack"
            : (packs.find((pack) => pack.code === season)?.name || season || "pack");
        const safePack = String(packLabel).replace(/[^\w.-]+/g, "_");
        downloadCsv(
            `word-wheel-top-users-${safePack}.csv`,
            isNonSeason
                ? [
                      "rank",
                      "type",
                      "app",
                      "platform",
                      "name",
                      "email",
                      "completedPuzzles",
                      "plays",
                      "updatedAt",
                      "cloudUserId",
                      "sessionId",
                  ]
                : [
                      "rank",
                      "type",
                      "app",
                      "platform",
                      "name",
                      "email",
                      "level",
                      "updatedAt",
                      "cloudUserId",
                      "sessionId",
                  ],
            rows.map((row) => {
                const isGuest = !row?.cloudUserId;
                const name = isGuest ? wordWheelGuestPlayerLabel(row) : (row?.name || "");
                const email = row?.email || "";
                const appId = row?.app || WORD_WHEEL_QUEST_APP_ID;
                const platform =
                    row?.platform === "android"
                        ? "Android"
                        : row?.platform === "apple"
                          ? "Apple"
                          : "";
                const base = [
                    csvNumber(row?.rank),
                    row?.type === "SIGNED" ? "Signed in" : "Anonymous",
                    appId,
                    platform,
                    name,
                    email,
                ];
                if (isNonSeason) {
                    return [
                        ...base,
                        csvNumber(
                            Number.isFinite(Number(row?.completedPuzzles))
                                ? row.completedPuzzles
                                : row?.level
                        ),
                        csvNumber(row?.plays),
                        formatDateTime(row?.updatedAt),
                        row?.cloudUserId || "",
                        row?.sessionId || "",
                    ];
                }
                return [
                    ...base,
                    csvNumber(row?.level),
                    formatDateTime(row?.updatedAt),
                    row?.cloudUserId || "",
                    row?.sessionId || "",
                ];
            })
        );
    }, [isNonSeason, packs, rows, season]);

    const columns = useMemo(
        () => [
            {
                field: "rank",
                headerName: "Rank",
                width: 80,
                sortable: false,
            },
            {
                field: "type",
                headerName: "Type",
                width: 130,
                sortable: false,
                renderCell: (params) =>
                    params.value === "SIGNED" ? (
                        <Chip size="small" color="primary" label="Signed in" />
                    ) : (
                        <Chip size="small" variant="outlined" label="Anonymous" />
                    ),
            },
            {
                field: "app",
                headerName: "App",
                width: 180,
                sortable: false,
                valueGetter: (_value, row) => row?.app || WORD_WHEEL_QUEST_APP_ID,
                renderCell: (params) => {
                    const appId = params.row?.app || WORD_WHEEL_QUEST_APP_ID;
                    const displayName =
                        appId === WORD_WHEEL_QUEST_APP_ID ? WORD_WHEEL_QUEST_APP_NAME : appId;
                    const platform = params.row?.platform;
                    const platformLabel =
                        platform === "android"
                            ? "Android"
                            : platform === "apple"
                              ? "Apple"
                              : null;
                    return (
                        <Box sx={{ py: 0.5, lineHeight: 1.25 }}>
                            <Typography variant="body2" component="div" noWrap title={displayName}>
                                {displayName}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                noWrap
                                title={appId}
                            >
                                {appId}
                                {platformLabel ? ` · ${platformLabel}` : ""}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: "name",
                headerName: "Name / Email",
                flex: 1.4,
                minWidth: 260,
                sortable: false,
                valueGetter: (_value, row) =>
                    [row?.name, row?.email].filter(Boolean).join(" ") ||
                    wordWheelGuestPlayerLabel(row),
                renderCell: (params) => {
                    const isGuest = !params.row?.cloudUserId;
                    const nameLabel = isGuest
                        ? wordWheelGuestPlayerLabel(params.row)
                        : params.row?.name || "—";
                    const email = params.row?.email || (isGuest ? null : "—");
                    const href = wordWheelPlayerPlaysPath({
                        cloudUserId: params.row?.cloudUserId,
                        sessionId: params.row?.sessionId,
                        name: nameLabel,
                    });
                    const content = (
                        <Box sx={{ py: 0.5, lineHeight: 1.25 }}>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                    fontWeight: 600,
                                    fontFamily:
                                        params.row?.sessionId && !params.row?.cloudUserId
                                            ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
                                            : "inherit",
                                    wordBreak: "break-all",
                                    whiteSpace: "normal",
                                }}
                            >
                                {nameLabel}
                            </Typography>
                            {email ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                    noWrap
                                    title={email}
                                >
                                    {email}
                                </Typography>
                            ) : null}
                        </Box>
                    );
                    if (!params.row?.cloudUserId && !params.row?.sessionId) {
                        return content;
                    }
                    return (
                        <Link
                            component={RouterLink}
                            to={href}
                            underline="hover"
                            color="inherit"
                            onClick={(event) => event.stopPropagation()}
                            sx={{ display: "block", width: "100%" }}
                        >
                            {content}
                        </Link>
                    );
                },
            },
            isNonSeason
                ? {
                      field: "completedPuzzles",
                      headerName: "Completed",
                      width: 120,
                      sortable: false,
                      valueGetter: (value, row) =>
                          Number.isFinite(Number(value)) ? Number(value) : Number(row?.level) || 0,
                      valueFormatter: (value) => formatNumber(value),
                  }
                : {
                      field: "level",
                      headerName: "Level",
                      width: 100,
                      sortable: false,
                  },
            ...(isNonSeason
                ? [
                      {
                          field: "plays",
                          headerName: "Plays",
                          width: 100,
                          sortable: false,
                          valueFormatter: (value) => formatNumber(value),
                      },
                  ]
                : []),
            {
                field: "updatedAt",
                headerName: "Updated",
                width: 170,
                sortable: false,
                valueFormatter: (value) => formatDateTime(value),
            },
        ],
        [isNonSeason]
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="ww-top-users-season-label">Packs</InputLabel>
                    <Select
                        labelId="ww-top-users-season-label"
                        label="Packs"
                        value={season}
                        onChange={(event) => resetPage(() => setSeason(event.target.value))}
                    >
                        <MenuItem value={WORD_WHEEL_SEASON_NONE}>No pack</MenuItem>
                        {packOptions(packs).map((pack) => (
                            <MenuItem key={pack.code} value={pack.code}>
                                {pack.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) => resetPage(() => setExcludeTest(event.target.checked))}
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) => resetPage(() => setExcludeStaff(event.target.checked))}
                        />
                    }
                    label="Exclude staff"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeNoEmail}
                            onChange={(event) => resetPage(() => setExcludeNoEmail(event.target.checked))}
                        />
                    }
                    label="Exclude no email"
                />

                <Typography variant="body2" color="text.secondary">
                    {language} · Word Wheel Quest app players
                </Typography>

                <Box sx={{ ml: "auto" }}>
                    <CsvDownloadButton
                        label="Download top users CSV"
                        disabled={rows.length === 0}
                        onClick={handleDownloadCsv}
                    />
                </Box>
            </Box>

            <TextField
                size="small"
                fullWidth
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={WORD_WHEEL_PLAYER_SEARCH_PLACEHOLDER}
                label="Search player"
                sx={{ mb: 2, maxWidth: 560 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isNonSeason
                    ? "No pack: published puzzles that are not in a pack. Ranked by unique completed puzzles, then plays."
                    : "Highest current level in this pack first. Signed-in players use their saved progress for the pack; guests are ranked by the next pack puzzle they would play. Click a player to open their play list."}
                {" "}
                Only Word Wheel Quest app players are listed (Android/iOS device token for that app, or a RevenueCat id from the current app build). Website-only word wheel players are omitted.
                {" "}
                Search by the RevenueCat App User ID on play details after the customer
                plays on a current app build or completes a signed-in purchase ({`$RCAnonymousID:…`}),
                the session UUID, a name, or an email.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!error && result && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {isNonSeason ? (
                        <>
                            <SummaryTile
                                label="Published puzzles"
                                value={formatNumber(result.puzzleCount)}
                                hint="No pack"
                            />
                            <SummaryTile
                                label="Plays"
                                value={formatNumber(result.playCount)}
                                hint="All plays with no pack"
                            />
                            <SummaryTile
                                label="Completions"
                                value={formatNumber(result.completedPuzzles)}
                                hint="Unique puzzles per player, summed"
                            />
                        </>
                    ) : null}
                    <SummaryTile label="Players" value={formatNumber(result.total)} hint="Signed-in and guests" />
                    <SummaryTile label="Signed in" value={formatNumber(result.signedCount)} />
                    <SummaryTile label="Anonymous" value={formatNumber(result.guestCount)} hint="Guest sessions" />
                </Box>
            )}

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                getRowHeight={() => "auto"}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={pageModel}
                onPaginationModelChange={setPageModel}
                pageSizeOptions={[15, 25, 50]}
                autoHeight
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                sx={{
                    "& .MuiDataGrid-cell": {
                        py: 1,
                        alignItems: "center",
                    },
                }}
                localeText={{
                    noRowsLabel: loading
                        ? "Loading…"
                        : (isNonSeason ? "No players for puzzles with no pack." : "No players for this pack."),
                }}
            />
        </Box>
    );
}

function SummaryTile({ label, value, hint }) {
    return (
        <Box sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}
